/**
 * MobileSettingsList — the merged mobile Settings screen, styled as a flat
 * grouped list (à la Linear): Workspace · Notifications · Display · Language,
 * then the destinations without a bottom-nav tab, then Log out + version.
 *
 * Each row is label-left / value-right; pickers open a headless Menu, links use
 * a chevron, and the notification row reflects live push-registration status.
 */

import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronEndIcon } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { locales } from "@/lib/i18n/config";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useThemeStore, type ThemePref } from "@/shared/theme/useTheme";
import { useFcmRegistration } from "@/modules/notifications/FcmRegistrationProvider";
import { useLogoutMutation } from "@/shared/services/useLogoutMutation";
import { deregisterFcmTokenFromBackend } from "@/lib/firebase/fcm";

type StoreOption = { storeSlug: string; storeName: string };

type Props = {
  locale: string;
  storeSlug: string;
  storeName: string;
  availableStores: StoreOption[];
  isSwitchingStore: boolean;
  onSwitchStore: (slug: string) => void;
  loginUrl: string;
};

export function MobileSettingsList({
  locale,
  storeSlug,
  storeName,
  availableStores,
  isSwitchingStore,
  onSwitchStore,
  loginUrl,
}: Props) {
  const t = useTranslations();
  const canSwitchStores = availableStores.length > 1;

  return (
    <div className="flex flex-col gap-5">
      {/* Workspace */}
      <SettingsGroup>
        {canSwitchStores ? (
          <PickerRow
            label={t(keys.seller.storeSwitcherLabel)}
            value={storeName}
            loading={isSwitchingStore}
            options={availableStores.map((s) => ({
              id: s.storeSlug,
              label: s.storeName,
            }))}
            selectedId={storeSlug}
            onSelect={onSwitchStore}
          />
        ) : (
          <StaticRow label={t(keys.seller.storeSwitcherLabel)} value={storeName} />
        )}
      </SettingsGroup>

      {/* Preferences */}
      <SettingsGroup>
        <NotificationsRow />
        <ThemeRow />
        <LanguageRow currentLocale={locale} />
      </SettingsGroup>

      {/* Destinations without their own bottom-nav tab */}
      <SettingsGroup>
        <LinkRow
          label={t(keys.seller.storeSettingsTitle)}
          to={getSellerPath(locale, storeSlug, "/settings")}
        />
        <LinkRow
          label={t(keys.seller.accountTitle)}
          to={getSellerPath(locale, storeSlug, "/profile")}
        />
        <LinkRow
          label={t(keys.checkoutLinks.pageTitle)}
          to={getSellerPath(locale, storeSlug, "/account/checkout-links")}
        />
        <LinkRow
          label={t(keys.seller.storeEditorSimpleTitle)}
          to={getSellerPath(locale, storeSlug, "/store/editor/simple")}
        />
      </SettingsGroup>

      {/* Log out */}
      <SettingsGroup>
        <LogoutRow redirectTo={loginUrl} />
      </SettingsGroup>

      <div className="pb-2 text-center text-[11.5px] text-[color:var(--text-muted)]">
        {t(keys.common.appName)} · {storeSlug} · v2.4.0
      </div>
    </div>
  );
}

// ─── Row primitives ────────────────────────────────────────────────────────

function SettingsGroup({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)]">
      {children}
    </div>
  );
}

const ROW =
  "flex min-h-[52px] w-full items-center justify-between gap-3 border-b border-[color:var(--border-light)] px-4 last:border-b-0";
const ROW_LABEL = "text-[15px] font-medium text-[color:var(--text-primary)]";
const ROW_VALUE =
  "flex min-w-0 items-center gap-1.5 text-[14px] text-[color:var(--text-muted)]";

function StaticRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={ROW}>
      <span className={ROW_LABEL}>{label}</span>
      <span className="truncate text-[14px] text-[color:var(--text-muted)]">{value}</span>
    </div>
  );
}

function LinkRow({ label, to }: { label: string; to: string }) {
  return (
    <Link
      to={to}
      className={`${ROW} no-underline transition-colors hover:bg-[color:var(--surface-alt)]`}
    >
      <span className={ROW_LABEL}>{label}</span>
      <span aria-hidden="true" className="shrink-0 text-[color:var(--text-muted)]">
        <ChevronEndIcon />
      </span>
    </Link>
  );
}

function PickerRow({
  label,
  value,
  options,
  selectedId,
  onSelect,
  loading = false,
  valueTone,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
  loading?: boolean;
  valueTone?: string;
}) {
  return (
    <Menu as="div" className="relative">
      <MenuButton
        className={`${ROW} text-start transition-colors hover:bg-[color:var(--surface-alt)] disabled:opacity-60`}
        disabled={loading}
      >
        <span className={ROW_LABEL}>{label}</span>
        <span className={ROW_VALUE} style={valueTone ? { color: valueTone } : undefined}>
          <span className="truncate">{value}</span>
          <ChevronsUpDownIcon />
        </span>
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 mt-1 w-[200px] rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-1 shadow-[0_12px_36px_rgba(0,0,0,0.16)] focus:outline-none"
      >
        {options.map((option) => {
          const active = option.id === selectedId;
          return (
            <MenuItem key={option.id}>
              <button
                type="button"
                onClick={() => onSelect(option.id)}
                className="flex w-full items-center justify-between gap-3 rounded-[8px] px-3 py-2.5 text-start text-[14px] text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--surface-alt)]"
              >
                <span className="truncate">{option.label}</span>
                {active ? <CheckIcon /> : null}
              </button>
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
}

// ─── Concrete rows ─────────────────────────────────────────────────────────

function ThemeRow() {
  const t = useTranslations();
  const pref = useThemeStore((s) => s.pref);
  const setPref = useThemeStore((s) => s.setPref);
  const options: { id: ThemePref; label: string }[] = [
    { id: "light", label: t(keys.common.themeLight) },
    { id: "dark", label: t(keys.common.themeDark) },
    { id: "system", label: t(keys.common.themeSystem) },
  ];
  const current = options.find((o) => o.id === pref)?.label ?? "";

  return (
    <PickerRow
      label={t(keys.common.theme)}
      value={current}
      options={options}
      selectedId={pref}
      onSelect={(id) => setPref(id as ThemePref)}
    />
  );
}

function LanguageRow({ currentLocale }: { currentLocale: string }) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const options = [
    { id: "en", label: t(keys.common.languageEnglish), native: "English" },
    { id: "ar", label: t(keys.common.languageArabic), native: "العربية" },
  ];
  const current = options.find((o) => o.id === currentLocale)?.native ?? "";

  const handleChange = (next: string) => {
    if (next === currentLocale) return;
    const path = pathname ?? "/";
    const pattern = new RegExp(`^/(${locales.join("|")})(/|$)`);
    const nextPath = pattern.test(path)
      ? path.replace(pattern, `/${next}$2`)
      : `/${next}${path.startsWith("/") ? "" : "/"}${path}`;
    navigate(nextPath, { replace: true });
  };

  return (
    <PickerRow
      label={t(keys.common.language)}
      value={current}
      options={options.map((o) => ({ id: o.id, label: o.native }))}
      selectedId={currentLocale}
      onSelect={handleChange}
    />
  );
}

function NotificationsRow() {
  const t = useTranslations();
  const { status, lastError, retry, requestPermissionAndRegister } =
    useFcmRegistration();

  let value: string;
  let tone: string | undefined;
  let onTap: (() => void) | null = null;

  switch (status) {
    case "registered":
      value = t(keys.notifications.pushStatusEnabled);
      tone = "var(--success-500)";
      break;
    case "registering":
      value = t(keys.notifications.pushStatusEnabling);
      break;
    case "prompt":
      value = t(keys.notifications.pushStatusNotEnabled);
      tone = "var(--gold-500)";
      onTap = () => void requestPermissionAndRegister();
      break;
    case "denied":
      value = t(keys.notifications.pushStatusBlocked);
      tone = "var(--error-500)";
      break;
    case "offline":
      value = t(keys.notifications.pushStatusOffline);
      onTap = () => retry();
      break;
    case "error":
      value =
        lastError?.reason === "config"
          ? t(keys.notifications.pushStatusUnavailableDevice)
          : t(keys.notifications.pushStatusTrouble);
      tone = "var(--error-500)";
      onTap = () => retry();
      break;
    case "unsupported":
      value = t(keys.notifications.pushStatusUnsupported);
      break;
    default:
      value = t(keys.notifications.pushStatusEnabling);
      break;
  }

  const content = (
    <>
      <span className={ROW_LABEL}>{t(keys.notifications.title)}</span>
      <span className={ROW_VALUE} style={tone ? { color: tone } : undefined}>
        <span className="truncate">{value}</span>
        {onTap ? <ArrowUpRightIcon /> : null}
      </span>
    </>
  );

  if (onTap) {
    return (
      <button
        type="button"
        onClick={onTap}
        className={`${ROW} text-start transition-colors hover:bg-[color:var(--surface-alt)]`}
      >
        {content}
      </button>
    );
  }
  return <div className={ROW}>{content}</div>;
}

function LogoutRow({ redirectTo }: { redirectTo: string }) {
  const t = useTranslations();
  const navigate = useNavigate();
  const logout = useLogoutMutation();

  const handleLogout = async () => {
    await deregisterFcmTokenFromBackend().catch(() => {});
    await logout.mutateAsync({});
    navigate(redirectTo, { replace: true });
  };

  return (
    <button
      type="button"
      onClick={() => void handleLogout()}
      disabled={logout.isPending}
      className={`${ROW} text-start transition-colors hover:bg-[color:var(--surface-alt)] disabled:opacity-60`}
    >
      <span className="text-[15px] font-semibold text-[color:var(--error-500)]">
        {t(keys.common.logout)}
      </span>
      <span aria-hidden="true" className="shrink-0 text-[color:var(--error-500)]">
        <LogoutIcon />
      </span>
    </button>
  );
}

// ─── Icons ─────────────────────────────────────────────────────────────────

const ICON = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function ChevronsUpDownIcon() {
  return (
    <svg {...ICON} width={16} height={16} className="shrink-0">
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  );
}

function ArrowUpRightIcon() {
  return (
    <svg {...ICON} width={16} height={16} className="shrink-0">
      <path d="M7 17 17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...ICON} className="shrink-0 text-[color:var(--accent-500)]">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg {...ICON}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
